// TUT-450 — os documentos: modelos e geração, lista, envio e download, retenção e guarda legal, e a cadeia de publicação.
import { demoApi } from "../demo-data";
import { enviarAoArmazenamento, type RelatorDeProgresso } from "../upload-transport";
import type {
  DocumentTemplate,
  DocumentTemplateField,
  DocumentTemplateListResponse,
  CreateDocumentTemplateInput,
  RenderedDocument,
  DocumentListResponse,
  RecorteDeDocumentos,
  ItemDoLote,
  PreparoDoDownloadEmLote,
  CorDeEtiqueta,
  EtiquetaDoCatalogo,
  EtiquetaDoDocumento,
  DocumentPublication,
  UpcomingDiscard,
  ProcessDetail,
  ProcessDocument,
  UploadDocumentInput,
} from "../types";
import { ApiError } from "../api-error";
import { comFatia, request, requestArquivo, sha256 } from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDosDocumentos = {
  // Templates de documento versionados (TUT-042).
  documentTemplates(
    includeInactive = false,
  ): Promise<DocumentTemplateListResponse> {
    if (useDemoApi) return demoApi.documentTemplates(includeInactive);
    const query = includeInactive ? "?includeInactive=true" : "";
    return request<DocumentTemplateListResponse>(
      `/documents/templates${query}`,
    );
  },
  documentTemplateFields(): Promise<{ items: DocumentTemplateField[] }> {
    if (useDemoApi) return demoApi.documentTemplateFields();
    return request<{ items: DocumentTemplateField[] }>(
      "/documents/templates/fields",
    );
  },
  createDocumentTemplate(
    input: CreateDocumentTemplateInput,
  ): Promise<DocumentTemplate> {
    if (useDemoApi) return demoApi.createDocumentTemplate(input);
    return request<DocumentTemplate>("/documents/templates", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  createDocumentTemplateVersion(
    familyId: string,
    body: string,
  ): Promise<DocumentTemplate> {
    if (useDemoApi)
      return demoApi.createDocumentTemplateVersion(familyId, body);
    return request<DocumentTemplate>(
      `/documents/templates/${encodeURIComponent(familyId)}/versions`,
      { method: "POST", body: JSON.stringify({ body }) },
    );
  },
  setDocumentTemplateActive(
    id: string,
    active: boolean,
  ): Promise<DocumentTemplate> {
    if (useDemoApi) return demoApi.setDocumentTemplateActive(id, active);
    return request<DocumentTemplate>(
      `/documents/templates/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify({ active }) },
    );
  },
  renderDocumentTemplate(
    id: string,
    input: { processId: string; expertiseId?: string },
  ): Promise<RenderedDocument> {
    if (useDemoApi) return demoApi.renderDocumentTemplate(id, input);
    return request<RenderedDocument>(
      `/documents/templates/${encodeURIComponent(id)}/render`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  /**
   * TUT-181 fatia 2 — a fatia é OPCIONAL: quem chama sem ela (o cartão do
   * hub, o seletor) continua recebendo o conjunto inteiro, que é o contrato
   * da porta. Só a tela de lista pede página.
   */
  documents(
    search = "",
    fatia: FatiaPedida = {},
    // TUT-204i: o recorte vai ao SERVIDOR. TUT-489a: os filtros por coluna
    // são listas, e cada valor repete a chave (`status=A&status=B`).
    recorte: RecorteDeDocumentos = {},
  ): Promise<DocumentListResponse> {
    if (useDemoApi) return demoApi.documents(search, fatia, recorte);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    for (const chave of [
      "category",
      "retentionClass",
      "processId",
      "status",
      "confidentiality",
      "tag",
    ] as const) {
      for (const valor of recorte[chave] ?? []) params.append(chave, valor);
    }
    if (recorte.updatedWithinDays) {
      params.set("updatedWithinDays", String(recorte.updatedWithinDays));
    }
    // TUT-204c: `comFatia` no lugar da montagem à mão — a cópia manual escrevia
    // só `page`/`pageSize` e deixaria `sort`/`dir` para trás em silêncio.
    return request<DocumentListResponse>(
      `/documents${comFatia(params, fatia)}`,
    );
  },
  // TUT-489b — as etiquetas: o catálogo do escritório e as do documento.
  documentTags(incluirDesativadas = false): Promise<{ items: EtiquetaDoCatalogo[] }> {
    if (useDemoApi) return demoApi.documentTags(incluirDesativadas);
    return request<{ items: EtiquetaDoCatalogo[] }>(
      `/documents/tags${incluirDesativadas ? "?incluirDesativadas=true" : ""}`,
    );
  },
  createDocumentTag(input: { nome: string; cor: CorDeEtiqueta }): Promise<EtiquetaDoCatalogo> {
    if (useDemoApi) return demoApi.createDocumentTag(input);
    return request<EtiquetaDoCatalogo>("/documents/tags", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateDocumentTag(
    tagId: string,
    mudanca: { nome?: string; cor?: CorDeEtiqueta; ativa?: boolean },
  ): Promise<EtiquetaDoCatalogo> {
    if (useDemoApi) return demoApi.updateDocumentTag(tagId, mudanca);
    return request<EtiquetaDoCatalogo>(`/documents/tags/${encodeURIComponent(tagId)}`, {
      method: "PATCH",
      body: JSON.stringify(mudanca),
    });
  },
  setDocumentTags(
    documentId: string,
    tagIds: readonly string[],
  ): Promise<{ items: EtiquetaDoDocumento[] }> {
    if (useDemoApi) return demoApi.setDocumentTags(documentId, tagIds);
    return request<{ items: EtiquetaDoDocumento[] }>(
      `/documents/${encodeURIComponent(documentId)}/tags`,
      { method: "PUT", body: JSON.stringify({ tagIds }) },
    );
  },
  // TUT-489c — a seleção em massa: o preparo do .zip (sem efeito), o .zip
  // (com a trilha de cada arquivo), mover de classe e etiquetar.
  prepararDownloadDoLote(documentIds: readonly string[]): Promise<PreparoDoDownloadEmLote> {
    if (useDemoApi) return demoApi.prepararDownloadDoLote(documentIds);
    return request<PreparoDoDownloadEmLote>("/documents/lote/download/preparo", {
      method: "POST",
      body: JSON.stringify({ documentIds }),
    });
  },
  async baixarLote(
    documentIds: readonly string[],
  ): Promise<{ arquivo: Blob; nome: string; incluidos: number; deFora: number }> {
    if (useDemoApi) return demoApi.baixarLote(documentIds);
    const { arquivo, cabecalhos } = await requestArquivo(
      "/documents/lote/download",
      { documentIds },
      "application/zip",
    );
    return {
      arquivo,
      nome: "documentos.zip",
      incluidos: Number(cabecalhos.get("X-Documentos-Incluidos") ?? "0"),
      deFora: Number(cabecalhos.get("X-Documentos-De-Fora") ?? "0"),
    };
  },
  moverClasseDoLote(
    documentIds: readonly string[],
    category: string,
  ): Promise<{ items: ItemDoLote[] }> {
    if (useDemoApi) return demoApi.moverClasseDoLote(documentIds, category);
    return request<{ items: ItemDoLote[] }>("/documents/lote/classe", {
      method: "POST",
      body: JSON.stringify({ documentIds, category }),
    });
  },
  etiquetarLote(
    documentIds: readonly string[],
    adicionar: readonly string[],
    remover: readonly string[],
  ): Promise<{ items: ItemDoLote[] }> {
    if (useDemoApi) return demoApi.etiquetarLote(documentIds, adicionar, remover);
    return request<{ items: ItemDoLote[] }>("/documents/lote/etiquetas", {
      method: "POST",
      body: JSON.stringify({ documentIds, adicionar, remover }),
    });
  },
  async uploadDocument(
    processId: string,
    input: UploadDocumentInput,
    aoProgredir?: RelatorDeProgresso,
  ): Promise<ProcessDetail["documents"][number]> {
    if (useDemoApi) {
      aoProgredir?.({ fase: "preparando" });
      const documento = await demoApi.uploadDocument(processId, input);
      aoProgredir?.({ fase: "concluindo" });
      return documento;
    }
    aoProgredir?.({ fase: "preparando" });
    const checksumSha256 = await sha256(input.file);
    const versionPath = input.documentId
      ? `/processes/${encodeURIComponent(processId)}/documents/${encodeURIComponent(input.documentId)}/uploads`
      : `/processes/${encodeURIComponent(processId)}/documents/uploads`;
    const prepared = await request<{
      uploadId: string;
      upload: {
        url: string;
        method: "PUT";
        headers: Record<string, string>;
      };
    }>(versionPath, {
      method: "POST",
      body: JSON.stringify({
        ...(!input.documentId
          ? {
              title: input.title,
              category: input.category,
              retentionClass: input.retentionClass,
              confidentiality: input.confidentiality,
            }
          : {}),
        originalName: input.file.name,
        mimeType: input.file.type,
        sizeBytes: input.file.size,
        checksumSha256,
      }),
    });
    const uploadResponse = await enviarAoArmazenamento({
      url: prepared.upload.url,
      method: prepared.upload.method,
      headers: prepared.upload.headers,
      corpo: input.file,
      aoProgredir,
    });
    if (!uploadResponse.ok) {
      throw new ApiError(
        "O armazenamento recusou o arquivo. Tente novamente.",
        uploadResponse.status,
      );
    }
    aoProgredir?.({ fase: "concluindo" });
    return request<ProcessDetail["documents"][number]>(
      `/documents/uploads/${encodeURIComponent(prepared.uploadId)}/complete`,
      { method: "POST" },
    );
  },
  // TUT-013b: baixar/visualizar passa pela API — cada clique vira linha de
  // auditoria (quem, qual versão, quando) ANTES de existir URL.
  documentDownloadLink(
    documentId: string,
    versionId: string,
    modo: "download" | "inline",
  ): Promise<{ url: string; expiresInSeconds: number }> {
    if (useDemoApi)
      return demoApi.documentDownloadLink(documentId, versionId, modo);
    const sufixo = modo === "inline" ? "?modo=inline" : "";
    return request<{ url: string; expiresInSeconds: number }>(
      `/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/download${sufixo}`,
    );
  },
  // Retenção por classe + legal hold (TUT-044).
  upcomingDocumentDiscards(): Promise<{ items: UpcomingDiscard[] }> {
    if (useDemoApi) return demoApi.upcomingDocumentDiscards();
    return request<{ items: UpcomingDiscard[] }>(
      "/documents/retention/upcoming",
    );
  },
  extendDocumentRetention(
    documentId: string,
    input: { days: number; note?: string },
  ): Promise<{ id: string }> {
    if (useDemoApi) return demoApi.extendDocumentRetention(documentId, input);
    return request<{ id: string }>(
      `/documents/${encodeURIComponent(documentId)}/retention/extensions`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  setDocumentLegalHold(
    documentId: string,
    reason: string | null,
  ): Promise<ProcessDocument> {
    if (useDemoApi) return demoApi.setDocumentLegalHold(documentId, reason);
    return request<ProcessDocument>(
      `/documents/${encodeURIComponent(documentId)}/legal-hold`,
      { method: "PATCH", body: JSON.stringify({ reason }) },
    );
  },
  // Publicação com cadeia por classe (TUT-043).
  documentPublication(
    documentId: string,
    versionId: string,
  ): Promise<{ publication: DocumentPublication | null }> {
    if (useDemoApi) return demoApi.documentPublication(versionId);
    return request<{ publication: DocumentPublication | null }>(
      `/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/publication`,
    );
  },
  submitDocumentPublication(
    documentId: string,
    versionId: string,
  ): Promise<DocumentPublication> {
    if (useDemoApi)
      return demoApi.submitDocumentPublication(documentId, versionId);
    return request<DocumentPublication>(
      `/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/publication`,
      { method: "POST" },
    );
  },
  reviewDocumentPublication(
    documentId: string,
    versionId: string,
    input: { approve: boolean; reason?: string },
  ): Promise<DocumentPublication> {
    if (useDemoApi) return demoApi.reviewDocumentPublication(versionId, input);
    return request<DocumentPublication>(
      `/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/publication/review`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  approveDocumentPublication(
    documentId: string,
    versionId: string,
  ): Promise<DocumentPublication> {
    if (useDemoApi) return demoApi.approveDocumentPublication(versionId);
    return request<DocumentPublication>(
      `/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/publication/approve`,
      { method: "POST" },
    );
  },
  deliverDocumentPublication(
    documentId: string,
    versionId: string,
    email: string,
  ): Promise<{ publication: DocumentPublication; delivered: boolean }> {
    if (useDemoApi) return demoApi.deliverDocumentPublication(versionId, email);
    return request<{ publication: DocumentPublication; delivered: boolean }>(
      `/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/publication/deliver`,
      { method: "POST", body: JSON.stringify({ email }) },
    );
  },
};
